import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BottomNavigation from '../../components/feature/BottomNavigation'
import { useTutorial } from '../../tutorial/TutorialProvider'
import { useAuth } from '../../contexts/AuthContext'
import { miniTutorialService, type MiniTutorialsState } from '../../services/miniTutorialService'

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resume } = useTutorial();
  const [activeCategory, setActiveCategory] = useState('geral');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [miniTutorialsState, setMiniTutorialsState] = useState<MiniTutorialsState>({});

  // Carregar estado dos mini tutoriais do banco
  useEffect(() => {
    if (user?.id) {
      miniTutorialService.getUserState(user.id)
        .then(setMiniTutorialsState)
        .catch((err) => console.error('Error loading mini tutorials state:', err));
    }
  }, [user?.id]);

  // Escutar mudanças de estado dos mini tutoriais em tempo real
  useEffect(() => {
    const handleStateChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ key: string; seen: boolean; enabled: boolean }>;
      const { key, seen, enabled } = customEvent.detail;
      
      console.log(`[HelpCenter] Mini tutorial ${key} mudou: seen=${seen}, enabled=${enabled}`);
      
      // Atualizar estado local imediatamente
      setMiniTutorialsState(prev => ({
        ...prev,
        [key]: { seen, enabled }
      }));
    };

    window.addEventListener('magistral:minitip:state-changed', handleStateChange);
    
    return () => {
      window.removeEventListener('magistral:minitip:state-changed', handleStateChange);
    };
  }, []);

  // Recarregar estado quando a página é focada (caso tenha lido mini tutoriais em outras páginas)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id) {
        console.log('[HelpCenter] Recarregando estado dos mini tutoriais...');
        miniTutorialService.getUserState(user.id)
          .then(setMiniTutorialsState)
          .catch((err) => console.error('Error reloading mini tutorials state:', err));
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  // Toggle individual de mini tutorial (verde ↔ cinza)
  const toggleMiniTutorial = async (key: string) => {
    if (!user?.id) return;
    
    const currentState = miniTutorialsState[key];
    const isCurrentlyEnabled = currentState?.enabled ?? true;

    // Atualizar UI otimisticamente
    setMiniTutorialsState(prev => ({
      ...prev,
      [key]: {
        seen: currentState?.seen ?? false,
        enabled: !isCurrentlyEnabled
      }
    }));

    // Persistir no banco
    const success = await miniTutorialService.toggleMiniTutorial(user.id, key);
    
    if (success) {
      // Notificar outras páginas da mudança
      window.dispatchEvent(new CustomEvent('magistral:minitip:state-changed', {
        detail: {
          key,
          seen: currentState?.seen ?? false,
          enabled: !isCurrentlyEnabled
        }
      }));
    } else {
      // Reverter em caso de erro
      setMiniTutorialsState(prev => ({
        ...prev,
        [key]: currentState || { seen: false, enabled: true }
      }));
    }
  };

  // Resetar todos os mini tutoriais
  const resetAllMiniTutorials = async () => {
    if (!user?.id) {
      alert('É necessário estar logado para resetar os mini tutoriais.');
      return;
    }

    const confirmed = window.confirm('Tem certeza que deseja resetar todos os mini tutoriais? Todos voltarão a aparecer.');
    if (!confirmed) return;

    const success = await miniTutorialService.resetAll(user.id);
    
    if (success) {
      // Atualizar estado local
      const resetState = miniTutorialService.getDefaultState();
      setMiniTutorialsState(resetState);
      
      // Notificar todas as páginas do reset
      window.dispatchEvent(new CustomEvent('magistral:minitip:all-reset'));
      
      alert('✅ Mini Tutoriais resetados com sucesso!');
    } else {
      alert('❌ Erro ao resetar mini tutoriais. Tente novamente.');
    }
  };

  const categories = [
    { id: 'geral', name: 'Geral', icon: 'ri-question-line' },
    { id: 'medicamentos', name: 'Medicamentos', icon: 'ri-medicine-bottle-line' },
    { id: 'pontuacao', name: 'Pontuação', icon: 'ri-star-line' },
    { id: 'conta', name: 'Conta', icon: 'ri-user-line' },
  ];

  const faqs: FAQItem[] = [
    {
      id: 1,
      question: 'Como funciona o sistema de pontuação?',
      answer: 'O sistema de pontuação recompensa você por manter hábitos saudáveis e seguir seu tratamento homeopático. Você ganha pontos por tomar medicamentos no horário, registrar eventos positivos de saúde e manter consistência no tratamento.',
      category: 'pontuacao'
    },
    {
      id: 2,
      question: 'Como adicionar um novo medicamento ao tratamento?',
      answer: 'Para adicionar um medicamento, vá até a seção "Medicamentos" no menu principal, toque no botão "+" e preencha as informações do medicamento, incluindo nome, dosagem e horários de administração.',
      category: 'medicamentos'
    },
    {
      id: 3,
      question: 'Posso usar o app offline?',
      answer: 'Sim! O Magistral funciona offline para a maioria das funcionalidades. Seus dados são salvos localmente e sincronizados quando você se conecta à internet novamente.',
      category: 'geral'
    },
    {
      id: 4,
      question: 'Como alterar minha senha?',
      answer: 'Vá em Configurações > Perfil > Segurança e Privacidade e toque em "Alterar Senha". Você precisará inserir sua senha atual e a nova senha duas vezes para confirmar.',
      category: 'conta'
    },
    {
      id: 5,
      question: 'O que fazer se esquecer de tomar um medicamento?',
      answer: 'Se você esquecer de tomar um medicamento, registre isso no app assim que lembrar. O sistema ajustará sua pontuação e lembretes automaticamente. Consulte sempre seu médico homeopata para orientações específicas.',
      category: 'medicamentos'
    },
    {
      id: 6,
      question: 'Como funciona o diário de saúde?',
      answer: 'O diário de saúde permite registrar como você se sente diariamente, sintomas, melhorias e eventos relacionados à sua saúde. Essas informações ajudam a acompanhar seu progresso no tratamento.',
      category: 'geral'
    },
    {
      id: 7,
      question: 'Posso perder pontos? Como?',
      answer: 'Sim, você pode perder pontos por não tomar medicamentos no horário, registrar eventos negativos de saúde ou usar medicamentos alopáticos. Isso faz parte do sistema de gamificação para incentivar hábitos saudáveis.',
      category: 'pontuacao'
    },
    {
      id: 8,
      question: 'Como excluir minha conta?',
      answer: 'Para excluir sua conta, vá em Configurações > Perfil > Segurança e Privacidade > Excluir Conta. Lembre-se que esta ação é irreversível e todos os seus dados serão permanentemente removidos.',
      category: 'conta'
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'geral' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between p-6">
          <button
            onClick={() => navigate('/settings')}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-gray-600 text-lg"></i>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Centro de Ajuda</h1>
          <div className="w-10 h-10"></div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-4">
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="Buscar ajuda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="px-6 mb-6">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                activeCategory === category.id
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              <i className={`${category.icon} mr-2 text-sm`}></i>
              <span className="text-sm font-medium">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* FAQ List */}
      <div className="px-6 pb-6">
        {filteredFAQs.length > 0 ? (
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-800 pr-4">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-gray-400 transition-transform ${
                      expandedFAQ === faq.id ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {expandedFAQ === faq.id && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
              <i className="ri-search-line text-gray-400 text-2xl"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">Nenhum resultado encontrado</h3>
            <p className="text-gray-600 text-sm">
              Tente usar palavras-chave diferentes ou navegue pelas categorias.
            </p>
          </div>
        )}
      </div>

      {/* Mini Tutoriais (atalhos) */}
      <div className="px-6 pb-6">
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Mini Tutoriais</h2>
          <p className="text-sm text-gray-500 mb-4">
            🟢 <strong>Verde</strong> = Habilitado (aparecerá)  |  ⚫ <strong>Cinza</strong> = Desabilitado (não aparecerá)
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'dashboardSummary', label: 'Resumo de Hoje' },
              { key: 'footer-remedies', label: 'Biblioteca de Remédios' },
              { key: 'footer-profile', label: 'Página de Perfil' },
              { key: 'footer-sos', label: 'SOS' },
              { key: 'member-points-history', label: 'Histórico de Pontos (Membro)' },
              { key: 'member-adherence', label: 'Adesão (Membro)' },
              { key: 'member-quiz', label: 'Quiz (Membro)' },
              { key: 'member-streak-treatment', label: 'Dias sem Interrupção (Membro)' },
              { key: 'member-streak-allopathic', label: 'Dias sem Alopático (Membro)' },
              { key: 'member-streak-illness', label: 'Dias sem Doenças (Membro)' },
              { key: 'member-badges-button', label: 'Meus Badges (Botão)' },
              { key: 'member-badges-card', label: 'Conquistas (Card)' },
              { key: 'member-today-homeopathies', label: 'Homeopatias de Hoje' },
              { key: 'member-treatments-history', label: 'Histórico de Tratamentos' },
              { key: 'member-medical-record', label: 'Registro Médico' },
              { key: 'member-health-diary', label: 'Diário de Saúde' },
              { key: 'member-reminders-log', label: 'Log de Lembretes' },
            ].map((item) => {
              const state = miniTutorialsState[item.key];
              const isEnabled = state?.enabled ?? true;
              
              return (
                <button
                  key={item.key}
                  onClick={() => toggleMiniTutorial(item.key)}
                  className={`w-full py-2 px-3 rounded-xl text-sm text-left font-medium transition-all ${
                    isEnabled
                      ? 'bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-500 border-2 border-gray-300'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <button
              onClick={resetAllMiniTutorials}
              className="w-full py-2 px-3 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl text-sm text-gray-700 font-medium"
            >
              Resetar Mini Tutoriais (este dispositivo)
            </button>
          </div>
        </div>
      </div>

      {/* Contact Support */}
      <div className="px-6 pb-6">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-6 text-white">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mr-4">
              <i className="ri-customer-service-2-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Ainda precisa de ajuda?</h3>
              <p className="text-emerald-100 text-sm">Nossa equipe está aqui para ajudar</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => resume('first_run')}
              className="w-full py-3 bg-white text-emerald-700 rounded-2xl font-medium flex items-center justify-center"
            >
              <i className="ri-compass-3-line mr-2"></i>
              Reabrir Guided Tutorial
            </button>
            <button className="w-full py-3 bg-white bg-opacity-20 rounded-2xl font-medium flex items-center justify-center">
              <i className="ri-mail-line mr-2"></i>
              Enviar Email
            </button>
            <button className="w-full py-3 bg-white bg-opacity-20 rounded-2xl font-medium flex items-center justify-center">
              <i className="ri-phone-line mr-2"></i>
              Falar no WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* App Info */}
      <div className="px-6 pb-24">
        <div className="bg-white rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-3xl flex items-center justify-center mb-4 mx-auto">
            <i className="ri-information-line text-emerald-500 text-2xl"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Magistral v1.0</h3>
          <p className="text-gray-600 text-sm mb-4">
            Seu companheiro para o tratamento homeopático
          </p>
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <span>Última atualização: Jan 2024</span>
            <span>•</span>
            <span>Build 1.0.0</span>
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
}